import { useState, useEffect, useCallback } from 'react';
import { Box, Flex, IconButton, Button, Select } from '@chakra-ui/react';
import { MdDelete, MdSearch, MdEdit } from 'react-icons/md';

import Page from '../components/common/Page';
import Api, { ApiCommand } from '../lib/Api';
import useToastNotification from '../lib/hooks/useToastNotification';
import StrongTable from '../components/common/StrongTable';
import StrongPagination from '../components/common/StrongPagination';
import useApi from '../lib/hooks/useApi';
import urlConstants from '../lib/constants/url.constants';
import StrongInput from '../components/common/StrongInput';
import ThumbnailPreview from '../components/common/ThumbnailPreview';
import EditExerciseModal from '../components/exercises/EditExerciseModal';
import StrongSelect from '../components/common/StrongSelect';

const EXERCISE_COLUMNS = [
  {
    key: 'title',
    label: 'Title',
  },
  {
    key: 'description',
    label: 'Description',
  },
  {
    key: 'thumbnail',
    label: 'Thumbnail',
    render: (value: string) => <ThumbnailPreview imageUrl={value} />,
  },
  {                                                                        
    key: 'gender',
    label: 'Gender',
    render: (value: string) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '',
  },  
];

const {
  createExercise: createExerciseUrl,
  getExercises: getExercisesUrl,
  getExercisestitle: getExercisesTitleUrl,
  getCategoriestitle: getCategoriesTitleUrl,
  getEquipmentstitle: getEquipmentsTitleUrl,
  editExercise: editExerciseUrl,
  deleteExercise: deleteExerciseUrl,
  flushAllReferences: flushAllReferencesUrl,
} = urlConstants.exercises
const { getSectionstitle: getProgramsTitleUrl } = urlConstants.equipments;

const genderFilterOptions = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
];

const Exercises = () => {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [exercisestitle, setExercisesTitle] = useState<ExerciseTitle[]>([]);
  const [programstitle, setProgramsTitle] = useState<ProgramTitle[]>([]);

  const [Categoriestitle, setCategoriesTitle] = useState<CategoryTitle[]>([]);
  const [equipmentstitle, setEquipmentsTitle] = useState<EquipmentTitle[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [selectedExercise, setSelectedExercise] = useState<Exercise>();
  const [sortCriteria, setSortCriteria] = useState<string>('NewestAdded');
  const [selectedProgramFilters, setSelectedProgramFilters] = useState<string[]>([]);
  const [selectedGenderFilters, setSelectedGenderFilters] = useState<string[]>([]);
  const showToast = useToastNotification();

  const { loading: isGetExercisesLoading, sendRequest: getExercises } =
    useApi<GetExercisesResopnse>();
  const { sendRequest: getExercisestitle } =
    useApi<ExerciseTitle[]>();
  const { sendRequest: getCategoriestitle } =
    useApi<CategoryTitle[]>();
  const { sendRequest: getEquipmentstitle } =
    useApi<EquipmentTitle[]>();
  const { sendRequest: getProgramstitle } =
    useApi<ProgramTitle[]>();
  const { sendRequest: createExercise } =
    useApi<any>();
  const { sendRequest: editExercise } =
    useApi<any>();
  const { sendRequest: deleteExercise } =
    useApi<any>();
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchExercises = useCallback(() => {
    getExercises({
      callback: (data: GetExercisesResopnse | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setExercises(data.exercises);
        setTotalPages(data.pagination.totalPages);
        setTotalCount(data.pagination.totalCount);
      },
      command: ApiCommand.GET,
      url: getExercisesUrl,
      options: {
        page: currentPage,
        limit: pageSize,
        search: searchQuery,
        sortBy: sortCriteria,
        programIds: selectedProgramFilters,
        genders: selectedGenderFilters,
        lite: true,
      },
    });
  }, [
    getExercises,
    currentPage,
    pageSize,
    searchQuery,
    sortCriteria,
    selectedProgramFilters,
    selectedGenderFilters,
    showToast,
  ]);

  const fetchCategoriesTitles = useCallback(() => {
    getCategoriestitle({
      callback: (data: CategoryTitle[] | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setCategoriesTitle(data);
      },
      command: ApiCommand.GET,
      url: getCategoriesTitleUrl,
    });
  }, [getCategoriestitle, showToast]);
  const fetchExercisesTitles = useCallback(() => {
    getExercisestitle({
      callback: (data: ExerciseTitle[] | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setExercisesTitle(data);
      },
      command: ApiCommand.GET,
      url: getExercisesTitleUrl,
    });
  }, [getExercisestitle, showToast]);
  const fetchEquipmentsTitles = useCallback(() => {
    getEquipmentstitle({
      callback: (data: EquipmentTitle[] | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setEquipmentsTitle(data);
      },
      command: ApiCommand.GET,
      url: getEquipmentsTitleUrl,
    });
  }, [getEquipmentstitle, showToast]);
  const fetchProgramsTitles = useCallback(() => {
    getProgramstitle({
      callback: (data: ProgramTitle[] | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setProgramsTitle(data);
      },
      command: ApiCommand.GET,
      url: getProgramsTitleUrl,
    });
  }, [getProgramstitle, showToast]);

  useEffect(() => {
    fetchExercises();
    fetchExercisesTitles();
    fetchCategoriesTitles();
    fetchEquipmentsTitles();
    fetchProgramsTitles();
  }, [
    fetchExercises,
    fetchExercisesTitles,
    fetchCategoriesTitles,
    fetchEquipmentsTitles,
    fetchProgramsTitles,
  ]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleProgramFilterChange = (programIds: string[]) => {
    setSelectedProgramFilters(programIds);
    setCurrentPage(1);
  };

  const handleGenderFilterChange = (genders: string[]) => {
    setSelectedGenderFilters(genders);
    setCurrentPage(1);
  };

  const onSaveExercise = (
    title: string,
    description: string,
    thumbnail: File | null,
    videoId: string,
    selectedcategories: string[],
    selectedequipments: string[],
    genders: string[],
    type: string,
    selectedRelatedExercises: string[],
  ) => {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description || '');
    formData.append('videoId', videoId || '');
    formData.append('categories', JSON.stringify(selectedcategories));
    formData.append('equipments', JSON.stringify(selectedequipments));
    formData.append('relatedExercises', JSON.stringify(selectedRelatedExercises));
    if (thumbnail) {
      formData.append('thumbnail', thumbnail);
    }
    formData.append('genders', JSON.stringify(genders));
    formData.append('gender', genders[0] || '');
    formData.append('type', type || 'exercise');
    if (selectedExercise) {
      formData.append('exerciseId', selectedExercise?._id || '');
      editExercise({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: 'Failed',
              description: error,
              status: 'error',
            });
            return;
          }
          showToast({
            title: 'Success',
            //TODO create toast.constants.ts file and create variables for all toast description
            description: 'Exercise updated successfully',
            status: 'success',
          });
          setIsOpenModal(false);
          fetchExercises();
        },
        command: ApiCommand.PUT,
        url: editExerciseUrl,
        options: formData,
      });
    } else {
      createExercise({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: 'Failed',
              description: error,
              status: 'error',
            });
            return;
          }
          showToast({
            title: 'Success',
            description: 'Exercise created successfully',
            status: 'success',
          });
          setIsOpenModal(false);
          fetchExercises();
        },
        command: ApiCommand.POST,
        url: createExerciseUrl,
        options: formData,
      });
    }
  };

  const onDeleteExercise = (exerciseId: string) => {
    if (!exerciseId) {
      showToast({
        title: 'Failed',
        description: 'Exercise ID is missing',
        status: 'error',
      });
      return;
    }

    deleteExercise({
      callback: (_data, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        showToast({
          title: 'Success',
          description: 'Exercise deleted successfully',
          status: 'success',
        });
        fetchExercises();
      },
      command: ApiCommand.DELETE,
      url: deleteExerciseUrl,
      options: {
        exerciseId,
      },
    });
  };

  const flushAndDeleteExercise = async (exerciseId: string) => {
    if (!exerciseId) {
      showToast({
        title: 'Failed',
        description: 'Exercise ID is missing',
        status: 'error',
      });
      return;
    }

    try {
      const response = await Api.client.delete<{
        message?: string;
        history?: { deletedCount?: number };
        programs?: { modifiedCount?: number };
      }>(`${flushAllReferencesUrl}/${exerciseId}`);

      const deletedCount = response.data?.history?.deletedCount ?? 0;
      const modifiedCount = response.data?.programs?.modifiedCount ?? 0;
      showToast({
        title: 'References flushed',
        description: `Removed ${deletedCount} history records and cleaned ${modifiedCount} program references. Proceeding to delete exercise.`,
        status: 'success',
      });
      onDeleteExercise(exerciseId);
    } catch (error: any) {
      showToast({
        title: 'Failed to flush history',
        description:
          error?.response?.data?.message || error?.message || 'Unable to flush history',
        status: 'error',
      });
    }
  };

  const onEditExercise = (exerciseId: string) => {
    if (!exerciseId) {
      showToast({
        title: 'Failed',
        description: 'Exercise ID is missing',
        status: 'error',
      });
      return;
    }

    setSelectedExercise(exercises.find(({ _id }) => _id === exerciseId));
    setIsOpenModal(true);
  };

  return (
    <Page>
      <Flex flexDirection='column' height='full' justifyContent='space-between'>
        <Box
          backgroundColor='white'
          borderRadius='lg'
          padding={2}
        >
          <Flex justifyContent='space-between' marginBottom={4} gap={3} flexWrap='wrap'>
            <Box flex='1' maxW='400px'>
              <StrongInput
                placeholder='Search'
                rightIcon={<MdSearch color='gray.500' />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </Box>
            <Box width={{ base: '100%', md: '280px' }}>
              <StrongSelect
                options={programstitle.map(({ title, _id }) => ({
                  label: title,
                  value: _id,
                }))}
                value={selectedProgramFilters.map((value) => {
                  const program = programstitle.find((item) => item._id === value);
                  return {
                    label: program?.title || 'Unknown Program',
                    value,
                  };
                })}
                onSelectChange={handleProgramFilterChange}
                placeholder='Filter by programs'
              />
            </Box>
            <Box width={{ base: '100%', md: '210px' }}>
              <StrongSelect
                options={genderFilterOptions}
                value={selectedGenderFilters.map((value) => ({
                  label: value.charAt(0).toUpperCase() + value.slice(1),
                  value,
                }))}
                onSelectChange={handleGenderFilterChange}
                placeholder='Filter by gender'
              />
            </Box>
            <Select
              value={sortCriteria}
              onChange={(e) => setSortCriteria(e.target.value)}
              width={{ base: '100%', md: '160px' }}
            >
              <option value='NameAtoZ'>A-Z</option>
              <option value='NameZtoA'>Z-A</option>
              <option value='NewestAdded'>Newest</option>
              <option value='OldestAdded'>Oldest</option>
            </Select>
            <Button
              colorScheme='primary'
              px={6}
              py={4}
              borderRadius='md'
              fontWeight='bold'
              _hover={{ bg: 'primary.300' }}
              onClick={() => {
                setSelectedExercise(undefined);
                setIsOpenModal(true);
              }}
            >
              Create Exercise
            </Button>
          </Flex>
          <StrongTable
            columns={EXERCISE_COLUMNS}
            data={exercises}
            loading={isGetExercisesLoading}
            actions={(row) => (
              <>
                <IconButton
                  aria-label='Edit Exercise'
                  icon={<MdEdit />}
                  variant='outline'
                  colorScheme='primary'
                  size='sm'
                  marginRight={4}
                  onClick={() => onEditExercise(row._id)}
                />
                <IconButton
                  aria-label='Delete Exercise'
                  icon={<MdDelete />}
                  variant='outline'
                  colorScheme='red'
                  size='sm'
                  marginRight={4}
                  onClick={() => onDeleteExercise(row._id)}
                />
                <Button
                  size='sm'
                  colorScheme='yellow'
                  variant='outline'
                  onClick={() => flushAndDeleteExercise(row._id)}
                >
                  Flush & Delete
                </Button>
              </>
            )}
          />
        </Box>

        <StrongPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </Flex>

      <EditExerciseModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        onSubmit={onSaveExercise}
        equipmentstitle={equipmentstitle}
        categoriestitle={Categoriestitle}
        exercisestitle={exercisestitle}
        selectedExercise={selectedExercise}
      />
    </Page>
  );
};

export default Exercises;
